import {
  EDGE_IDS,
  formatMm,
  mm,
  type CabinetInstance,
  type CabinetParams,
  type EdgeId,
  type EvaluatedCabinet,
  type FrontRow,
  type MaterialSlot,
  type PanelPart,
  type ProjectDocument,
} from '@scs/domain-engine';
import { decorViews, fmtPrice, useCatalogContext, type Catalog } from '../lib/catalog';
import { IntInput, MmInput } from '../lib/ui';
import type { EditorStore } from './store';

type Exec = EditorStore['exec'];

const SLOT_LABEL: Record<MaterialSlot, string> = {
  carcass: 'Korpus',
  front: 'Fronte',
  back: 'Leđa',
  'drawer-box': 'Kutija ladice',
  'drawer-bottom': 'Dno ladice',
  plinth: 'Sokla',
};

const FRONT_LAYOUTS: { id: string; label: string; rows: FrontRow[] }[] = [
  { id: 'none', label: 'Bez fronti', rows: [] },
  { id: 'door-left', label: 'Vrata, šarke lijevo', rows: [{ kind: 'door-left', height: null }] },
  { id: 'door-right', label: 'Vrata, šarke desno', rows: [{ kind: 'door-right', height: null }] },
  { id: 'double', label: 'Dvoja vrata', rows: [{ kind: 'double-door', height: null }] },
  { id: 'drawers2', label: '2 ladice', rows: [{ kind: 'drawer', height: null }, { kind: 'drawer', height: null }] },
  { id: 'drawers3', label: '3 ladice', rows: [{ kind: 'drawer', height: null }, { kind: 'drawer', height: null }, { kind: 'drawer', height: null }] },
  { id: 'drawers4', label: '4 ladice', rows: Array.from({ length: 4 }, () => ({ kind: 'drawer' as const, height: null })) },
  { id: 'drawer-door', label: 'Ladica + dvoja vrata', rows: [{ kind: 'drawer', height: mm(140) }, { kind: 'double-door', height: null }] },
];

function layoutId(rows: FrontRow[]): string {
  const key = JSON.stringify(rows);
  return FRONT_LAYOUTS.find((l) => JSON.stringify(l.rows) === key)?.id ?? 'custom';
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="prop-row">
      <label>{label}</label>
      {children}
    </div>
  );
}

export function CabinetProperties({ inst, exec, disabled }: { inst: CabinetInstance; exec: Exec; disabled: boolean }) {
  const p = inst.params;
  const setParam = <K extends keyof CabinetParams>(label: string, key: K, value: CabinetParams[K]) =>
    exec(label, (d) => {
      const c = d.cabinets.find((x) => x.id === inst.id)!;
      c.params[key] = value;
    });
  const mmRow = (label: string, key: keyof CabinetParams, min = 1, max = 4000) => (
    <Row label={label}>
      <MmInput label={label} value={p[key] as number} min={min} max={max} disabled={disabled || inst.locked} onCommit={(v) => setParam(label, key, v as never)} />
    </Row>
  );
  const edit = (label: string, fn: (c: CabinetInstance) => void) =>
    exec(label, (d) => {
      fn(d.cabinets.find((x) => x.id === inst.id)!);
    });
  const lock = disabled || inst.locked;

  return (
    <div>
      <div className="prop-group">
        <h3>Element</h3>
        <Row label="Naziv">
          <input className="input" style={{ height: 26 }} value={inst.name} disabled={lock} onKeyDown={(e) => e.stopPropagation()} onChange={(e) => edit('Naziv', (c) => (c.name = e.target.value.slice(0, 200) || c.id))} />
        </Row>
        <Row label="ID">
          <span className="mono faint">{inst.id}</span>
        </Row>
      </div>
      <div className="prop-group">
        <h3>Dimenzije korpusa</h3>
        {mmRow('Širina', 'width', 100)}
        {mmRow('Visina korpusa', 'height', 100)}
        {mmRow('Dubina', 'depth', 100)}
        {mmRow('Sokla', 'plinthHeight', 0, 300)}
        {mmRow('Nogice', 'legsHeight', 0, 300)}
        <Row label="Ukupna visina">
          <span className="mono">{formatMm(p.height + p.plinthHeight + p.legsHeight)} mm</span>
        </Row>
      </div>
      <div className="prop-group">
        <h3>Debljine</h3>
        {mmRow('Stranice', 'sideThickness', 6, 60)}
        {mmRow('Dno', 'bottomThickness', 6, 60)}
        {mmRow('Vrh', 'topThickness', 6, 60)}
        {mmRow('Leđa', 'backThickness', 0, 30)}
        {mmRow('Police', 'shelfThickness', 6, 60)}
        {mmRow('Pregrade', 'dividerThickness', 6, 60)}
        {mmRow('Fronte', 'frontThickness', 6, 60)}
      </div>
      <div className="prop-group">
        <h3>Konstrukcija</h3>
        <Row label="Dno">
          <select className="input" disabled={lock} value={p.bottomConstruction} onChange={(e) => setParam('Konstrukcija dna', 'bottomConstruction', e.target.value as CabinetParams['bottomConstruction'])}>
            <option value="between-sides">Između stranica</option>
            <option value="under-sides">Ispod stranica</option>
          </select>
        </Row>
        <Row label="Vrh">
          <select className="input" disabled={lock} value={p.topConstruction} onChange={(e) => setParam('Konstrukcija vrha', 'topConstruction', e.target.value as CabinetParams['topConstruction'])}>
            <option value="between-sides">Između stranica</option>
            <option value="over-sides">Preko stranica</option>
            <option value="none">Bez vrha</option>
          </select>
        </Row>
        <Row label="Leđa">
          <select
            className="input"
            disabled={lock}
            value={p.back.mode}
            onChange={(e) => edit('Način leđa', (c) => (c.params.back = { ...c.params.back, mode: e.target.value as CabinetParams['back']['mode'] }))}
          >
            <option value="groove">U žlijebu</option>
            <option value="overlay">Nalijepljena</option>
            <option value="none">Bez leđa</option>
          </select>
        </Row>
        {p.back.mode === 'groove' && (
          <>
            <Row label="Uvlačenje žlijeba">
              <MmInput label="Uvlačenje žlijeba" value={p.back.grooveInset} min={0} max={100} disabled={lock} onCommit={(v) => edit('Uvlačenje žlijeba', (c) => (c.params.back.grooveInset = v))} />
            </Row>
            <Row label="Dubina žlijeba">
              <MmInput label="Dubina žlijeba" value={p.back.grooveDepth} min={1} max={20} disabled={lock} onCommit={(v) => edit('Dubina žlijeba', (c) => (c.params.back.grooveDepth = v))} />
            </Row>
          </>
        )}
        <Row label="Pregrade">
          <IntInput label="Pregrade" value={p.dividers} min={0} max={20} disabled={lock} onCommit={(v) => setParam('Broj pregrada', 'dividers', v)} />
        </Row>
        <Row label="Police po pregradi">
          <IntInput label="Police" value={p.shelves.count} min={0} max={30} disabled={lock} onCommit={(v) => edit('Broj polica', (c) => (c.params.shelves.count = v))} />
        </Row>
        <Row label="Uvučenje police">
          <MmInput label="Uvučenje police" value={p.shelves.frontSetback} min={0} max={200} disabled={lock} onCommit={(v) => edit('Uvučenje police', (c) => (c.params.shelves.frontSetback = v))} />
        </Row>
      </div>
      <div className="prop-group">
        <h3>Fronte</h3>
        <Row label="Raspored">
          <select className="input" disabled={lock} value={layoutId(p.fronts.rows)} onChange={(e) => edit('Raspored fronti', (c) => (c.params.fronts.rows = structuredClone(FRONT_LAYOUTS.find((l) => l.id === e.target.value)?.rows ?? [])))}>
            {FRONT_LAYOUTS.map((l) => (
              <option key={l.id} value={l.id}>
                {l.label}
              </option>
            ))}
            {layoutId(p.fronts.rows) === 'custom' && <option value="custom">Prilagođeno</option>}
          </select>
        </Row>
        <Row label="Montaža">
          <select className="input" disabled={lock} value={p.fronts.mounting} onChange={(e) => edit('Montaža fronti', (c) => (c.params.fronts.mounting = e.target.value as 'overlay' | 'inset'))}>
            <option value="overlay">Preklop (na korpus)</option>
            <option value="inset">Unutra (u korpus)</option>
          </select>
        </Row>
        <Row label="Vanjski zazor">
          <MmInput label="Vanjski zazor" value={p.fronts.gapOuter} min={0} max={20} step={0.5} disabled={lock} onCommit={(v) => edit('Vanjski zazor', (c) => (c.params.fronts.gapOuter = v))} />
        </Row>
        <Row label="Zazor između">
          <MmInput label="Zazor između" value={p.fronts.gapBetween} min={0} max={20} step={0.5} disabled={lock} onCommit={(v) => edit('Zazor između fronti', (c) => (c.params.fronts.gapBetween = v))} />
        </Row>
        {p.fronts.rows.some((r) => r.kind === 'drawer') && (
          <>
            <Row label="Vodilica">
              <span className="faint" title={p.drawerSystem.label}>
                Generička (ručna)
              </span>
            </Row>
            <Row label="Bočni razmak vodilice">
              <MmInput label="Bočni razmak" value={p.drawerSystem.sideClearance} min={0} max={50} step={0.5} disabled={lock} onCommit={(v) => edit('Razmak vodilice', (c) => (c.params.drawerSystem.sideClearance = v))} />
            </Row>
            <Row label="Dužina vodilice">
              <MmInput label="Dužina vodilice" value={p.drawerSystem.nominalLength} min={200} max={800} step={50} disabled={lock} onCommit={(v) => edit('Dužina vodilice', (c) => (c.params.drawerSystem.nominalLength = v))} />
            </Row>
          </>
        )}
      </div>
      <MaterialSlots inst={inst} exec={exec} disabled={lock} />
      <div className="prop-group">
        <h3>Položaj</h3>
        {(['X', 'Y', 'Z'] as const).map((axis, i) => (
          <Row key={axis} label={`Pozicija ${axis}`}>
            <MmInput
              label={`Pozicija ${axis}`}
              value={inst.transform.position[i]!}
              min={-20000}
              max={20000}
              step={10}
              disabled={lock}
              onCommit={(v) => edit(`Pomjeranje ${axis}`, (c) => (c.transform.position[i] = v))}
            />
          </Row>
        ))}
        <Row label="Okruženje">
          <select className="input" disabled={lock} value={inst.environment} onChange={(e) => edit('Okruženje', (c) => (c.environment = e.target.value as 'dry' | 'wet'))} title="Mokri čvor: izloženi rezovi bez kanta se zaptivaju">
            <option value="dry">Suho</option>
            <option value="wet">Mokri čvor (sudoper, kupatilo)</option>
          </select>
        </Row>
        <Row label="Rotacija">
          <select className="input" disabled={lock} value={inst.transform.rotationY} onChange={(e) => edit('Rotacija', (c) => (c.transform.rotationY = Number(e.target.value) as 0 | 90 | 180 | 270))}>
            {[0, 90, 180, 270].map((r) => (
              <option key={r} value={r}>
                {r}°
              </option>
            ))}
          </select>
        </Row>
      </div>
    </div>
  );
}

function DecorOptions({ cat }: { cat: Catalog }) {
  const views = decorViews(cat);
  const own = views.filter((v) => !v.isDemo);
  const demo = views.filter((v) => v.isDemo);
  return (
    <>
      {own.length > 0 && (
        <optgroup label="Katalog radionice">
          {own.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </optgroup>
      )}
      <optgroup label="Demo dekori (nisu stvarni proizvodi)">
        {demo.map((m) => (
          <option key={m.id} value={m.id}>
            {m.label}
          </option>
        ))}
      </optgroup>
    </>
  );
}

function MaterialSlots({ inst, exec, disabled }: { inst: CabinetInstance; exec: Exec; disabled: boolean }) {
  const cat = useCatalogContext();
  return (
    <div className="prop-group">
      <h3>Materijali</h3>
      {(Object.keys(SLOT_LABEL) as MaterialSlot[]).map((slot) => (
        <Row key={slot} label={SLOT_LABEL[slot]}>
          <select
            className="input"
            disabled={disabled}
            value={inst.materials[slot] ?? ''}
            onChange={(e) =>
              exec(`Materijal: ${SLOT_LABEL[slot]}`, (d) => {
                const c = d.cabinets.find((x) => x.id === inst.id)!;
                if (e.target.value) c.materials[slot] = e.target.value;
                else delete c.materials[slot];
              })
            }
          >
            <option value="">Iz projekta</option>
            <DecorOptions cat={cat} />
          </select>
        </Row>
      ))}
    </div>
  );
}

const GENERIC_BANDS = [
  { key: 'g-04-22', label: 'Generička 0,4 × 22 mm', thickness: 400, width: 22000 },
  { key: 'g-08-22', label: 'Generička 0,8 × 22 mm', thickness: 800, width: 22000 },
  { key: 'g-1-23', label: 'Generička 1 × 23 mm', thickness: 1000, width: 23000 },
  { key: 'g-2-22', label: 'Generička 2 × 22 mm', thickness: 2000, width: 22000 },
  { key: 'g-2-42', label: 'Generička 2 × 42 mm', thickness: 2000, width: 42000 },
];

/** Band choice: catalog product or a generic thickness/width (product chosen later). */
export function BandSelect({ value, onChange, disabled, label }: { value: { productId: string | null; thickness: number; width: number }; onChange(v: { productId: string | null; thickness: number; width: number }): void; disabled: boolean; label: string }) {
  const cat = useCatalogContext();
  const current = value.productId ?? GENERIC_BANDS.find((g) => g.thickness === value.thickness && g.width === value.width)?.key ?? 'custom';
  return (
    <select
      className="input"
      aria-label={label}
      disabled={disabled}
      value={current}
      onChange={(e) => {
        const p = cat.edges.find((x) => x.id === e.target.value);
        if (p) return onChange({ productId: p.id, thickness: p.thickness, width: p.width });
        const g = GENERIC_BANDS.find((x) => x.key === e.target.value);
        if (g) onChange({ productId: null, thickness: g.thickness, width: g.width });
      }}
    >
      {cat.edges.length > 0 && (
        <optgroup label="Katalog">
          {cat.edges.map((p) => {
            const art = cat.articles.find((a) => a.productKind === 'edge' && a.productId === p.id);
            return (
              <option key={p.id} value={p.id}>
                {p.code} {p.thickness / 1000}×{p.width / 1000} {art ? `· ${fmtPrice(art)}` : ''}
              </option>
            );
          })}
        </optgroup>
      )}
      <optgroup label="Bez proizvoda (izabrati kasnije)">
        {GENERIC_BANDS.map((g) => (
          <option key={g.key} value={g.key}>
            {g.label}
          </option>
        ))}
        {current === 'custom' && <option value="custom">{`${value.thickness / 1000} × ${value.width / 1000} mm`}</option>}
      </optgroup>
    </select>
  );
}

export function ProjectProperties({ doc, exec, disabled }: { doc: ProjectDocument; exec: Exec; disabled: boolean }) {
  const cat = useCatalogContext();
  return (
    <div>
      <div className="prop-group">
        <h3>Projekat</h3>
        <Row label="Profil kantiranja">
          <select className="input" disabled={disabled} value={doc.settings.edgePolicy} onChange={(e) => exec('Profil kantiranja', (d) => (d.settings.edgePolicy = e.target.value as ProjectDocument['settings']['edgePolicy']))}>
            <option value="workshop-standard">Standard radionice</option>
            <option value="visible-open-doors">Vidljive uz otvorena vrata</option>
            <option value="all-exposed">Sve izložene ivice</option>
            <option value="manual">Ručno</option>
          </select>
        </Row>
        <Row label="Mjere za narudžbu">
          <select className="input" disabled={disabled} value={doc.settings.measurementProfileId} onChange={(e) => exec('Profil mjera', (d) => (d.settings.measurementProfileId = e.target.value))}>
            <option value="order-finished">Gotova mjera (radnja oduzima kant)</option>
            <option value="order-substrate">Sirova podloga</option>
            {cat.profiles.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.verified ? ' ✓' : ' (neprovjeren)'}
              </option>
            ))}
          </select>
        </Row>
        <Row label="Kant (standard)">
          <BandSelect label="Kant standard" disabled={disabled} value={doc.settings.edgeBands.standard} onChange={(v) => exec('Standardna traka', (d) => (d.settings.edgeBands.standard = v))} />
        </Row>
        <Row label="Kant (fronte)">
          <BandSelect label="Kant fronte" disabled={disabled} value={doc.settings.edgeBands.front} onChange={(v) => exec('Traka za fronte', (d) => (d.settings.edgeBands.front = v))} />
        </Row>
        <Row label="Valuta">
          <select className="input" disabled={disabled} value={doc.settings.currency} onChange={(e) => exec('Valuta', (d) => (d.settings.currency = e.target.value as 'BAM' | 'EUR'))}>
            <option value="BAM">BAM</option>
            <option value="EUR">EUR</option>
          </select>
        </Row>
      </div>
      <div className="prop-group">
        <h3>Podrazumijevani materijali</h3>
        {(Object.keys(SLOT_LABEL) as MaterialSlot[]).map((slot) => (
          <Row key={slot} label={SLOT_LABEL[slot]}>
            <select className="input" disabled={disabled} value={doc.materials[slot]} onChange={(e) => exec(`Materijal projekta: ${SLOT_LABEL[slot]}`, (d) => (d.materials[slot] = e.target.value))}>
              <DecorOptions cat={cat} />
            </select>
          </Row>
        ))}
        <p className="faint" style={{ fontSize: 11 }}>
          Demo dekori su originalni proceduralni uzorci, ne stvarni proizvodi dobavljača.
        </p>
      </div>
    </div>
  );
}

const DECISION_LABEL = { band: 'Kant', none: 'Bez kanta', unresolved: 'Neriješeno', factory: 'Fabrička ivica', seal: 'Zaptivanje reza' } as const;

export function PartProperties({ part, cab, exec, disabled }: { part: PanelPart; cab: EvaluatedCabinet; exec: Exec; disabled: boolean }) {
  const decisions = cab.edges.find((e) => e.partId === part.id)?.decisions;
  const setEdge = (edge: EdgeId, value: string) =>
    exec(`Kant ${part.localKey} ${edge}`, (d) => {
      const c = [...d.cabinets, ...d.assemblies].find((x) => x.id === cab.instance.id)!;
      const key = `${part.localKey}:${edge}`;
      if (value === 'auto') delete c.edgeOverrides[key];
      else c.edgeOverrides[key] = { decision: value as 'band' | 'none' | 'unresolved' | 'factory' | 'seal' };
    });
  const setBand = (edge: EdgeId, band: { productId: string | null; thickness: number; width: number }) =>
    exec(`Traka ${part.localKey} ${edge}`, (d) => {
      const c = [...d.cabinets, ...d.assemblies].find((x) => x.id === cab.instance.id)!;
      c.edgeOverrides[`${part.localKey}:${edge}`] = { decision: 'band', band };
    });
  return (
    <div className="prop-group">
      <h3>Dio: {part.label}</h3>
      <Row label="ID">
        <span className="mono faint">{part.localKey}</span>
      </Row>
      <Row label="Dužina × širina">
        <span className="mono">
          {formatMm(part.length)} × {formatMm(part.width)}
        </span>
      </Row>
      <Row label="Debljina">
        <span className="mono">{formatMm(part.thickness)} mm</span>
      </Row>
      <Row label="God">
        <span>{part.grain === 'length' ? 'Po dužini' : part.grain === 'width' ? 'Po širini' : 'Bez goda'}</span>
      </Row>
      {part.generic && <p className="faint" style={{ fontSize: 11 }}>Generički dio — ručni model okova, bez CNC tvrdnje.</p>}
      <EdgeDiagram part={part} decisions={decisions} />
      {decisions &&
        EDGE_IDS.map((e) => {
          const d = decisions[e];
          return (
            <div key={e} className="edge-row">
              <span className="mono">{e}</span>
              <span>
                {DECISION_LABEL[d.decision]} {d.band ? `${formatMm(d.band.thickness)} mm` : ''} <span className="faint">({d.mode === 'manual' ? 'ručno' : 'auto'})</span>
              </span>
              <select className="input" style={{ height: 24 }} disabled={disabled} value={d.mode === 'manual' ? d.decision : 'auto'} onChange={(ev) => setEdge(e, ev.target.value)} aria-label={`Ivica ${e}`}>
                <option value="auto">Auto</option>
                <option value="band">Kant</option>
                <option value="none">Bez kanta</option>
                <option value="factory">Fabrička ivica</option>
                <option value="seal">Zaptivanje reza</option>
              </select>
              <span className="reason">{d.reason}</span>
              {d.mode === 'manual' && d.decision === 'band' && d.band && (
                <span style={{ gridColumn: '2 / 4' }}>
                  <BandSelect label={`Traka ${e}`} disabled={disabled} value={d.band} onChange={(b) => setBand(e, b)} />
                </span>
              )}
            </div>
          );
        })}
    </div>
  );
}

/** Unambiguous edge map: L-edges run along the length, W-edges along the width (see docs/edge-convention.md). */
function EdgeDiagram({ part, decisions }: { part: PanelPart; decisions?: Record<EdgeId, { decision: string }> }) {
  const W = 220;
  const ratio = Math.max(0.15, Math.min(1, part.width / part.length));
  const h = Math.round(110 * ratio) + 20;
  const x0 = 30;
  const y0 = 18;
  const w = W - 60;
  const color = (e: EdgeId) => {
    const d = decisions?.[e]?.decision;
    return d === 'band' ? 'var(--edge-band)' : d === 'unresolved' ? 'var(--edge-unresolved)' : 'var(--edge-none)';
  };
  return (
    <svg className="edge-diagram" width={W} height={h + 40} role="img" aria-label="Dijagram ivica">
      <rect x={x0} y={y0} width={w} height={h} fill="var(--bg-elev2)" stroke="none" />
      <line x1={x0} y1={y0 + h} x2={x0 + w} y2={y0 + h} stroke={color('L1')} strokeWidth={4} />
      <line x1={x0} y1={y0} x2={x0 + w} y2={y0} stroke={color('L2')} strokeWidth={4} />
      <line x1={x0} y1={y0} x2={x0} y2={y0 + h} stroke={color('W1')} strokeWidth={4} />
      <line x1={x0 + w} y1={y0} x2={x0 + w} y2={y0 + h} stroke={color('W2')} strokeWidth={4} />
      <text x={x0 + w / 2 - 8} y={y0 + h + 14}>L1</text>
      <text x={x0 + w / 2 - 8} y={y0 - 5}>L2</text>
      <text x={x0 - 22} y={y0 + h / 2 + 3}>W1</text>
      <text x={x0 + w + 6} y={y0 + h / 2 + 3}>W2</text>
      <text x={x0} y={y0 + h + 32}>
        dužina {formatMm(part.length)} → · širina {formatMm(part.width)} ↑
      </text>
    </svg>
  );
}
